/** Safely extract HTTP status from an unknown error (e.g. Axios-like response). */
export function getResponseStatus(error: unknown): number | undefined {
  if (typeof error === "object" && error !== null && "response" in error) {
    const resp = (error as { response: unknown }).response;
    if (typeof resp === "object" && resp !== null && "status" in resp) {
      const status = (resp as { status: unknown }).status;
      return typeof status === "number" ? status : undefined;
    }
  }
  return undefined;
}

/**
 * Extract translation key (tk) from an API error response.
 *
 * The generated SDK (openapi-ts) throws errors where the response body
 * is available at different paths depending on SDK version.
 * This function tries common shapes:
 *   - error.data.error.tk        (openapi-ts v0.x result.data envelope)
 *   - error.response.data.error.tk (axios-style)
 *   - error.body.error.tk
 */
export function extractErrorTk(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) return undefined;

  // Try: error.data.error.tk (openapi-ts throws with { data } on non-2xx)
  const asData = error as { data?: { error?: { tk?: string } } };
  if (typeof asData.data?.error?.tk === "string") return asData.data.error.tk;

  // Try: error.response.data.error.tk (axios-like)
  const asAxios = error as {
    response?: { data?: { error?: { tk?: string } } };
  };
  if (typeof asAxios.response?.data?.error?.tk === "string")
    return asAxios.response.data.error.tk;

  // Try: error.body.error.tk
  const asBody = error as { body?: { error?: { tk?: string } } };
  if (typeof asBody.body?.error?.tk === "string") return asBody.body.error.tk;

  // Try: error.error.tk (hey-api client returns raw response body as error)
  const asDirect = error as { error?: { tk?: string } };
  if (typeof asDirect.error?.tk === "string") return asDirect.error.tk;

  return undefined;
}

/**
 * Extract i18n interpolation params from an API error response.
 *
 * The backend pairs a `tk` with a `params` object when the message needs
 * values interpolated (e.g. a spend-denied billing error carries the blocking
 * reason's `{ frozenCredits, outstandingCredits }`). Params live next to `tk`,
 * either directly on the error node (`error.params`) or nested under
 * `error.details.params`. Tries the same envelope shapes as {@link extractErrorTk}.
 */
export function extractErrorParams(
  error: unknown,
): Record<string, unknown> | undefined {
  if (typeof error !== "object" || error === null) return undefined;

  const asRecord = (v: unknown): Record<string, unknown> | undefined =>
    typeof v === "object" && v !== null
      ? (v as Record<string, unknown>)
      : undefined;

  // Pull the params object off a single error node, checking `params` then
  // `details.params`.
  const fromErrorNode = (
    node: unknown,
  ): Record<string, unknown> | undefined => {
    const n = asRecord(node);
    if (!n) return undefined;
    const direct = asRecord(n["params"]);
    if (direct) return direct;
    const details = asRecord(n["details"]);
    return asRecord(details?.["params"]);
  };

  const obj = error as Record<string, unknown>;

  // error.data.error (openapi-ts) — also try error.data.error via chained access
  const dataErr = asRecord(asRecord(obj["data"])?.["error"]);
  const respErr = asRecord(
    asRecord(asRecord(obj["response"])?.["data"])?.["error"],
  );
  const bodyErr = asRecord(asRecord(obj["body"])?.["error"]);
  const directErr = asRecord(obj["error"]);

  return (
    fromErrorNode(directErr) ??
    fromErrorNode(dataErr) ??
    fromErrorNode(respErr) ??
    fromErrorNode(bodyErr)
  );
}

/**
 * Public error fields resolved from an execution's `errorDetails`.
 *
 * Phase 2 of #208 restructured `errorDetails` into:
 *   { public: { tk, code, message, inputRef }, message, internal: { ... } }
 *
 * This interface is the flattened view that UI components should consume.
 */
export interface ResolvedErrorDetails {
  tk?: string;
  params?: Record<string, unknown>;
  code?: string;
  message: string;
  correlationId?: string;
  eventId?: string;
  inputRef?: { nodeId: string; param: string; valuePreview?: string };
}

/**
 * Resolve an execution's `errorDetails` to a flat public-facing shape.
 *
 * Prefers `errorDetails.public.*` (Phase 2 shape) when present,
 * falls back to flat `errorDetails.*` for backwards compat.
 */
export function resolveErrorDetails(
  errorDetails: unknown,
): ResolvedErrorDetails {
  if (!errorDetails || typeof errorDetails !== "object") {
    return {
      message:
        typeof errorDetails === "string"
          ? errorDetails
          : "An unknown error occurred",
    };
  }

  const details = errorDetails as Record<string, unknown>;
  const pub =
    typeof details.public === "object" && details.public !== null
      ? (details.public as Record<string, unknown>)
      : null;

  const str = (v: unknown): string | undefined =>
    typeof v === "string" ? v : undefined;
  const params = pub?.params ?? details.params;

  return {
    tk: str(pub?.tk) ?? str(details.tk),
    params:
      params !== null && typeof params === "object" && !Array.isArray(params)
        ? (params as Record<string, unknown>)
        : undefined,
    code: str(pub?.code) ?? str(details.code),
    message:
      str(pub?.message) ?? str(details.message) ?? "An unknown error occurred",
    correlationId: str(pub?.correlationId) ?? str(details.correlationId),
    eventId: str(pub?.eventId) ?? str(details.eventId),
    inputRef: (() => {
      const ref = pub?.inputRef ?? details.inputRef;
      if (ref && typeof ref === "object") {
        const r = ref as Record<string, unknown>;
        if (typeof r.nodeId === "string" && typeof r.param === "string") {
          return {
            nodeId: r.nodeId,
            param: r.param,
            valuePreview:
              typeof r.valuePreview === "string" ? r.valuePreview : undefined,
          };
        }
      }
      return undefined;
    })(),
  };
}
